import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Plus, Pencil, Trash2, GripVertical } from "lucide-react";
import { toast } from "sonner";

// DB-backed editable dropdown: pick a value, plus inline Add / Edit / Delete / Bulk / Reorder (CEO only).
// endpoint e.g. "/income-sources"; items are [{id,name,usage,order}].
export function EditableListSelect({ value, onValueChange, endpoint, noun = "item", canManage = false,
  placeholder = "Select…", testPrefix = "list", refreshKey }) {
  const listKey = endpoint.replace(/^\//, "");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [dlg, setDlg] = useState(null); // "add" | "edit" | "bulk"
  const [input, setInput] = useState("");
  const [bulk, setBulk] = useState("");
  const [saving, setSaving] = useState(false);
  const [order, setOrder] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);

  const load = useCallback(() => {
    api.get(endpoint).then(({ data }) => setItems(data)).catch(() => {});
  }, [endpoint]);
  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => { if (dlg === "edit") setOrder(items); }, [dlg, items]);

  const nounCap = `${noun[0].toUpperCase()}${noun.slice(1)}`;

  const add = async () => {
    const name = input.trim();
    if (!name) return;
    setSaving(true);
    try {
      await api.post(endpoint, { name });
      setInput(""); load(); onValueChange(name); setDlg(null);
      toast.success(`${nounCap} added`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setSaving(false);
  };
  const addBulk = async () => {
    const text = bulk.trim();
    if (!text) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/master-lists/${listKey}/bulk`, { text });
      setBulk(""); load(); setDlg(null);
      toast.success(`Added ${data.added} ${noun}${data.added === 1 ? "" : "s"}${data.skipped ? `, ${data.skipped} skipped (duplicates)` : ""}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setSaving(false);
  };
  const rename = async (id, name) => {
    const v = (name || "").trim();
    if (!v) return;
    try { await api.put(`${endpoint}/${id}`, { name: v }); load(); toast.success("Updated"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    try { await api.delete(`${endpoint}/${id}`); load(); toast.success("Removed"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const persistOrder = async (arr) => {
    setOrder(arr);
    try { await api.post(`/master-lists/${listKey}/reorder`, { ids: arr.map((i) => i.id) }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const onDrop = (toIdx) => {
    if (dragIdx === null || dragIdx === toIdx) { setDragIdx(null); return; }
    const arr = [...order];
    const [moved] = arr.splice(dragIdx, 1);
    arr.splice(toIdx, 0, moved);
    setDragIdx(null);
    persistOrder(arr);
  };

  return (
    <>
      <Select open={open} onOpenChange={setOpen} value={value} onValueChange={onValueChange}>
        <SelectTrigger data-testid={`${testPrefix}-select`}><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent>
          {items.map((it) => (
            <div key={it.id} className="relative flex items-center pr-1">
              <SelectItem value={it.name} className="flex-1">{it.name}</SelectItem>
              {canManage && (
                <button type="button" data-testid={`${testPrefix}-delete-inline-${it.id}`}
                  onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); remove(it.id); }}
                  title={it.usage ? `In use by ${it.usage} record(s) — cannot delete` : `Delete ${noun}`}
                  className="shrink-0 p-1 rounded text-slate-400 hover:text-red-600 hover:bg-red-50">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
          {items.length === 0 && <div className="px-2 py-2 text-xs text-slate-400">No {noun}s yet — use Add below.</div>}
          {canManage && (
            <div className="mt-1 border-t border-slate-100 pt-1 flex gap-1" onPointerDown={(e) => e.preventDefault()}>
              <button type="button" data-testid={`${testPrefix}-add-btn`}
                onClick={() => { setOpen(false); setInput(""); setDlg("add"); }}
                className="flex-1 flex items-center justify-center gap-1 text-xs font-medium text-[#1E3A8A] hover:bg-slate-50 rounded px-2 py-1.5">
                <Plus className="h-3.5 w-3.5" /> Add
              </button>
              <button type="button" data-testid={`${testPrefix}-edit-btn`}
                onClick={() => { setOpen(false); setDlg("edit"); }}
                className="flex-1 flex items-center justify-center gap-1 text-xs font-medium text-slate-600 hover:bg-slate-50 rounded px-2 py-1.5">
                <Pencil className="h-3.5 w-3.5" /> Manage
              </button>
            </div>
          )}
        </SelectContent>
      </Select>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent data-testid={`${testPrefix}-dialog`}>
          <DialogHeader><DialogTitle>{dlg === "add" ? `Add ${noun}` : dlg === "bulk" ? `Bulk add ${noun}s` : `Manage ${noun}s`}</DialogTitle></DialogHeader>

          {dlg === "add" && (
            <div className="space-y-2">
              <Label className="text-sm">{nounCap} name</Label>
              <Input data-testid={`${testPrefix}-add-input`} value={input} autoFocus
                onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
                placeholder={`New ${noun}…`} />
            </div>
          )}

          {dlg === "bulk" && (
            <div className="space-y-2">
              <Label className="text-sm">Paste {noun}s — one per line or comma-separated</Label>
              <Textarea data-testid={`${testPrefix}-bulk-input`} value={bulk} rows={6} autoFocus
                onChange={(e) => setBulk(e.target.value)}
                placeholder={`${nounCap} A\n${nounCap} B\n${nounCap} C`} />
              <p className="text-xs text-slate-400">Duplicates are skipped automatically.</p>
            </div>
          )}

          {dlg === "edit" && (
            <div className="space-y-2">
              <div className="flex justify-end -mt-2">
                <Button size="sm" variant="outline" data-testid={`${testPrefix}-open-bulk`}
                  onClick={() => setDlg("bulk")} className="gap-1"><Plus className="h-3.5 w-3.5" /> Bulk add</Button>
              </div>
              <p className="text-xs text-slate-400">Drag <GripVertical className="inline h-3 w-3" /> to reorder.</p>
              <div className="space-y-2 max-h-80 overflow-auto">
                {order.length === 0 ? <p className="text-sm text-slate-400">No {noun}s yet.</p> :
                  order.map((it, idx) => (
                    <div key={it.id}
                      draggable onDragStart={() => setDragIdx(idx)}
                      onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(idx)}
                      className={`flex items-center gap-2 rounded ${dragIdx === idx ? "opacity-50" : ""}`}>
                      <GripVertical className="h-4 w-4 text-slate-300 cursor-grab shrink-0" />
                      <Row item={it} onRename={rename} onRemove={remove} testPrefix={testPrefix} noun={noun} />
                    </div>
                  ))}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(dlg === "bulk" ? "edit" : null)}>{dlg === "bulk" ? "Back" : "Close"}</Button>
            {dlg === "add" && <Button data-testid={`${testPrefix}-add-save`} onClick={add} disabled={saving || !input.trim()} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]">Add</Button>}
            {dlg === "bulk" && <Button data-testid={`${testPrefix}-bulk-save`} onClick={addBulk} disabled={saving || !bulk.trim()} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]">Add all</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Row({ item, onRename, onRemove, testPrefix, noun }) {
  const [v, setV] = useState(item.name);
  useEffect(() => { setV(item.name); }, [item.name]);
  const inUse = item.usage > 0;
  return (
    <div className="flex items-center gap-2 flex-1" data-testid={`${testPrefix}-row-${item.id}`}>
      <Input value={v} onChange={(e) => setV(e.target.value)} className="h-8 text-sm" data-testid={`${testPrefix}-edit-input-${item.id}`} />
      {inUse && <span className="text-[10px] whitespace-nowrap text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5" data-testid={`${testPrefix}-usage-${item.id}`}>in use · {item.usage}</span>}
      <Button size="sm" variant="outline" data-testid={`${testPrefix}-save-${item.id}`}
        onClick={() => onRename(item.id, v)} disabled={!v.trim() || v.trim() === item.name}>Save</Button>
      <Button size="sm" variant="ghost" data-testid={`${testPrefix}-delete-${item.id}`}
        onClick={() => onRemove(item.id)} disabled={inUse}
        title={inUse ? `In use by ${item.usage} record(s)` : `Delete ${noun}`}
        className="text-red-600 hover:text-red-700 disabled:opacity-40"><Trash2 className="h-4 w-4" /></Button>
    </div>
  );
}
